/**
 * Role → permission map, mirrored from the contract's `ROLE_PERMISSIONS`.
 *
 * The API is the *enforcement* authority; this copy is only ever used to decide what
 * the console renders. It is typed against the real `AdminRole` / `AdminPermission`
 * unions from `@genex/contracts`, so if the contract renames a permission or adds a
 * role, this map stops compiling — drift is caught at build time, not in production.
 * The server still gates every write, so a stale copy can only ever *under*-show; it
 * can never grant access the API would refuse.
 *
 * **What the paragraph above does not catch is an addition**, and six had accumulated.
 * `satisfies readonly AdminPermission[]` proves every entry is a real permission; nothing
 * proves every real permission is an entry, so `support:read`, `support:write`, `limits:read`,
 * `limits:write`, `pricing:read` and `pricing:write` were in the contract and absent here for
 * as long as they had existed. Nothing in the console gates on them yet, which is why the
 * omission was silent — and exactly why it was worth closing before something does: the first
 * page guarded on `limits:write` against this map would have locked out the compliance desk
 * that holds it.
 */
import type { AdminPermission, AdminRole } from './types.js';

export const ADMIN_ROLES = ['superadmin', 'compliance', 'finance', 'support'] as const;

export const ADMIN_PERMISSIONS = [
  'users:read',
  'users:write',
  'kyc:read',
  'kyc:review',
  'transactions:read',
  'reconciliation:read',
  'reconciliation:run',
  'providers:read',
  'providers:write',
  'flags:read',
  'flags:write',
  'rates:read',
  'rates:write',
  'admins:read',
  'admins:write',
  'audit:read',
  'support:read',
  'support:write',
  /** The per-tier transaction ceilings. A compliance instrument, not a treasury one. */
  'limits:read',
  'limits:write',
  /** The fee schedule, the deposit minimums, and a gift-card brand's naira-per-face rate. */
  'pricing:read',
  'pricing:write',
] as const satisfies readonly AdminPermission[];

export const ROLE_PERMISSIONS: Readonly<Record<AdminRole, readonly AdminPermission[]>> = {
  superadmin: [...ADMIN_PERMISSIONS],
  compliance: [
    'users:read',
    'users:write',
    'kyc:read',
    'kyc:review',
    'transactions:read',
    'providers:read',
    'flags:read',
    'rates:read',
    'audit:read',
    'support:read',
    // The tier ceilings are a compliance instrument: a licence obliges them, and the desk that
    // answers for that obligation must be able to move them without waiting for finance.
    'limits:read',
    'limits:write',
    'pricing:read',
  ],
  finance: [
    'users:read',
    'transactions:read',
    'reconciliation:read',
    'reconciliation:run',
    'providers:read',
    'providers:write',
    'flags:read',
    'rates:read',
    'rates:write',
    'audit:read',
    'support:read',
    'limits:read',
    // What the platform charges, as against what the market is doing.
    'pricing:read',
    'pricing:write',
  ],
  support: [
    'users:read',
    'kyc:read',
    'transactions:read',
    'reconciliation:read',
    'providers:read',
    'flags:read',
    'rates:read',
    'audit:read',
    'limits:read',
    'pricing:read',
    // Answering a customer is the job, and the console has always allowed it.
    'support:read',
    'support:write',
  ],
};

/** Whether a role holds a permission. `superadmin` short-circuits to everything. */
export function adminCan(role: AdminRole, permission: AdminPermission): boolean {
  if (role === 'superadmin') return true;
  return ROLE_PERMISSIONS[role].includes(permission);
}

/**
 * Whether a role holds *any* of a requirement — one permission, a list of them, or `null`
 * for "any authenticated admin".
 *
 * The list form exists for the pages that genuinely serve two grants. Gift cards is the
 * standing example: the API gates its redemption queue on `kyc:review` (compliance) and its
 * brand catalogue on `rates:write` (finance), because a rate is a treasury decision and the
 * console's tab layout is not a reason to hand pricing to the compliance desk. Guarding the
 * page on either grant alone locks out half the people it is for; guarding on any-of admits
 * both, and each surface inside then checks its own. Never used to widen a *write* — only to
 * decide whether a page renders at all.
 */
export function adminCanAny(
  role: AdminRole,
  permission: AdminPermission | readonly AdminPermission[] | null,
): boolean {
  if (permission === null) return true;
  if (typeof permission === 'string') return adminCan(role, permission);
  return permission.some((p) => adminCan(role, p));
}

export const ROLE_LABELS: Readonly<Record<AdminRole, string>> = {
  superadmin: 'Super admin',
  compliance: 'Compliance',
  finance: 'Finance',
  support: 'Support',
};
